/**
 * DateRangeCard — date inputs + inline quick-filter chips (CR-0015a / Δ-14a).
 *
 * Desktop/tablet (≥md): one row — `📅 From [..] To [..]   Today | Week | ⬇ Download`.
 * Mobile (<md): two rows — From + To same row; chips on next row.
 *
 * The "merge with QuickFilter" behaviour replaces the previous Δ-14 design
 * where DateRangeCard and QuickFilterRow were separate cards.
 */
import { Calendar } from "lucide-react";
import DownloadMenu from "@/components/report/DownloadMenu";
import { todayIso, daysAgoIso } from "@/utils/fmtTime";

export default function DateRangeCard({
  from,
  to,
  onFromChange,
  onToChange,
  tab,
  onRangeChange,
}) {
  return (
    <div
      data-testid="date-range-card"
      className="mx-4 mt-4 bg-white rounded-md shadow-sm border"
      style={{ borderColor: "#E5E7EB" }}
    >
      {/* Top section — dates + (md+ inline) chips */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Calendar size={20} style={{ color: "#22C55E" }} className="shrink-0" />

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label className="text-xs font-medium text-gray-600 shrink-0">From:</label>
          <input
            data-testid="date-from"
            type="date"
            value={from}
            onChange={(e) => onFromChange(e.target.value)}
            max={to || undefined}
            className="border rounded-md px-2 py-1.5 text-sm bg-gray-50 flex-1 min-w-0"
            style={{ borderColor: "#E5E7EB", color: "#111827" }}
          />
          <label className="text-xs font-medium text-gray-600 shrink-0">To:</label>
          <input
            data-testid="date-to"
            type="date"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            min={from || undefined}
            className="border rounded-md px-2 py-1.5 text-sm bg-gray-50 flex-1 min-w-0"
            style={{ borderColor: "#E5E7EB", color: "#111827" }}
          />
        </div>

        {/* Inline quick-filter chips on md+ only */}
        <div className="hidden md:flex items-center divide-x shrink-0 pl-2">
          <button
            data-testid="quick-today-inline"
            onClick={() => {
              const t = todayIso();
              onRangeChange(t, t);
            }}
            className="px-3 text-sm font-medium"
            style={{ color: "#2563EB" }}
          >
            Today
          </button>
          <button
            data-testid="quick-week-inline"
            onClick={() => onRangeChange(daysAgoIso(6), todayIso())}
            className="px-3 text-sm font-medium"
            style={{ color: "#2563EB" }}
          >
            Week
          </button>
          <div className="pl-3">
            <DownloadMenu tab={tab} from={from} to={to} />
          </div>
        </div>
      </div>

      {/* Mobile-only second row for chips (<md) */}
      <div
        data-testid="quick-filter-mobile"
        className="md:hidden flex items-center justify-around divide-x py-2.5 border-t"
        style={{ borderColor: "#E5E7EB" }}
      >
        <button
          data-testid="quick-today"
          onClick={() => {
            const t = todayIso();
            onRangeChange(t, t);
          }}
          className="flex-1 text-sm font-medium"
          style={{ color: "#2563EB" }}
        >
          Today
        </button>
        <button
          data-testid="quick-week"
          onClick={() => onRangeChange(daysAgoIso(6), todayIso())}
          className="flex-1 text-sm font-medium"
          style={{ color: "#2563EB" }}
        >
          Week
        </button>
        <div className="flex-1 flex justify-center">
          <DownloadMenu tab={tab} from={from} to={to} />
        </div>
      </div>
    </div>
  );
}
